import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../utils/helpers';

// Spinboard's own onboarding console. NOT part of the shop app: different
// login, different token, no shop context. It is reached at /admin and is
// deliberately plain — this is an internal tool, and the useful thing about it
// is that every line of the checklist is read from real state rather than
// ticked by hand.
//
// The token lives in its own storage key so signing into a shop cannot sign you
// into this, and vice versa.
const TOKEN_KEY = 'spinboard-admin-token';

const C = {
  bg: '#0f1724', panel: '#18212f', border: '#26303f',
  text: '#e8edf4', sub: '#9aa8bb', muted: '#6b7789',
  accent: '#F4622A', ok: '#2ecc71', partial: '#e0a030', todo: '#4a5566', danger: '#e74c3c',
};

const box = {
  background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18,
};
const input = {
  width: '100%', boxSizing: 'border-box', padding: '9px 11px', marginTop: 4,
  background: '#0c131e', border: `1px solid ${C.border}`, borderRadius: 7,
  color: C.text, fontSize: 14, fontFamily: 'inherit', outline: 'none',
};
const button = (primary) => ({
  padding: '9px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit',
  background: primary ? C.accent : 'transparent',
  color: primary ? '#fff' : C.sub,
  border: primary ? 'none' : `1px solid ${C.border}`,
});
const label = { fontSize: 12, color: C.sub, display: 'block', marginBottom: 10 };

// Every field carries a tip. Onboarding happens once per shop, months apart —
// nobody remembers on the second one what "opt-in evidence URL" meant on the
// first, and the cost of guessing is a rejected carrier registration a week
// later. `hint` is the short always-visible note; `tip` is the longer
// explanation on hover.
function Info({ tip }) {
  if (!tip) return null;
  return (
    <span title={tip} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 13, height: 13, marginLeft: 5, borderRadius: '50%',
      border: `1px solid ${C.muted}`, color: C.muted,
      fontSize: 9, lineHeight: 1, cursor: 'help', verticalAlign: 'middle',
    }}>i</span>
  );
}

function Field({ labelText, hint, tip, value, onChange, placeholder, maxLength, list, options, style }) {
  return (
    <label style={{ ...label, ...(style || {}) }}>
      {labelText}
      {hint && <span style={{ color: C.muted }}> — {hint}</span>}
      <Info tip={tip} />
      <input
        style={input}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
        list={list}
        title={tip || ''}
      />
      {/* A datalist suggests the values that have actually been accepted by the
          carrier before, without forbidding anything else — the accepted set is
          Twilio's, not ours, and hardcoding it as a dropdown would eventually
          be wrong. */}
      {list && options && (
        <datalist id={list}>
          {options.map(o => <option key={o} value={o} />)}
        </datalist>
      )}
    </label>
  );
}

function Shell({ children, centred }) {
  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text,
      fontFamily: "'Inter', -apple-system, system-ui, sans-serif", padding: '28px 20px',
      // The sign-in card is the only thing on its page, so it sits in the
      // middle of it rather than hanging off the left edge of a wide layout.
      ...(centred ? { display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}) }}>
      <div style={{ maxWidth: 880, margin: '0 auto', width: '100%', ...(centred ? { maxWidth: 380 } : {}) }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 22 }}>
          <span style={{ fontSize: 18, fontWeight: 700 }}>Spinboard</span>
          <span style={{ fontSize: 13, color: C.muted }}>onboarding console</span>
        </div>
        {children}
      </div>
    </div>
  );
}

// POST a JSON body to an unauthenticated console route; throws the server's
// error message on failure.
async function postJson(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const out = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(out.error || 'Something went wrong. Try again.');
  return out;
}

// "Forgot password?" from the sign-in card: emails a one-time link to the
// address on the login. Same answer whether or not the username exists.
function ForgotPassword({ onBack }) {
  const [username, setUsername] = useState('');
  const [done, setDone] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { setDone((await postJson('/api/admin/forgot', { username })).message); }
    catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <Shell centred>
      <form onSubmit={submit} style={box}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Reset your password</div>
        {done ? (
          <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.5, marginBottom: 14 }}>{done} The link works once, for one hour.</div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>We email a link to the address on your console login.</div>
            <label style={label}>Username
              <input style={input} value={username} onChange={e => setUsername(e.target.value)} autoFocus />
            </label>
            {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
            <button type="submit" style={button(true)} disabled={busy}>{busy ? 'Sending…' : 'Email me a link'}</button>
          </>
        )}
        <button type="button" onClick={onBack} style={{ ...button(false), marginLeft: done ? 0 : 8 }}>Back to sign-in</button>
      </form>
    </Shell>
  );
}

// The page the emailed link opens: /admin?reset=<token>.
function ResetPassword({ token, onDone }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError('The two passwords do not match.'); return; }
    setBusy(true); setError('');
    try { await postJson('/api/admin/reset', { token, newPassword: pw }); setDone(true); }
    catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <Shell centred>
      <form onSubmit={submit} style={box}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Choose a new password</div>
        {done ? (
          <>
            <div style={{ fontSize: 13, color: C.sub, marginBottom: 14 }}>Done. Sign in with your new password.</div>
            <button type="button" onClick={onDone} style={button(true)}>Go to sign-in</button>
          </>
        ) : (
          <>
            <label style={label}>New password <span style={{ color: C.muted }}>— at least 10 characters</span>
              <input style={input} type="password" value={pw} onChange={e => setPw(e.target.value)} autoFocus autoComplete="new-password" />
            </label>
            <label style={label}>The same again
              <input style={input} type="password" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" />
            </label>
            {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
            <button type="submit" style={button(true)} disabled={busy}>{busy ? 'Saving…' : 'Save password'}</button>
          </>
        )}
      </form>
    </Shell>
  );
}

// Signed in: change the password you have for one you choose.
function ChangePassword({ authFetch, onClose }) {
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError('The two new passwords do not match.'); return; }
    setBusy(true); setError('');
    try {
      const r = await authFetch('/api/admin/password', { method: 'POST', body: JSON.stringify({ currentPassword: current, newPassword: pw }) });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.error || 'Could not change the password.');
      setDone(true);
    } catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} style={{ ...box, marginBottom: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Change your password</div>
      {done ? (
        <div style={{ fontSize: 13, color: C.ok, marginBottom: 12 }}>Password changed. Use it the next time you sign in.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <label style={label}>Current password
            <input style={input} type="password" value={current} onChange={e => setCurrent(e.target.value)} autoComplete="current-password" />
          </label>
          <label style={label}>New password <span style={{ color: C.muted }}>— 10+ characters</span>
            <input style={input} type="password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" />
          </label>
          <label style={label}>New password again
            <input style={input} type="password" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" />
          </label>
        </div>
      )}
      {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      {!done && <button type="submit" style={button(true)} disabled={busy}>{busy ? 'Saving…' : 'Save new password'}</button>}
      <button type="button" onClick={onClose} style={{ ...button(false), marginLeft: done ? 0 : 8 }}>{done ? 'Close' : 'Cancel'}</button>
    </form>
  );
}

function Login({ onSignedIn }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Could not sign in.');
      localStorage.setItem(TOKEN_KEY, body.token);
      onSignedIn(body.admin);
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  if (forgot) return <ForgotPassword onBack={() => setForgot(false)} />;

  return (
    <Shell centred>
      <form onSubmit={submit} style={box}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Staff sign-in</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
          This is not a shop login. Shop staff sign in at the main app.
        </div>
        <label style={label}>Username
          <input style={input} value={username} onChange={e => setUsername(e.target.value)} autoFocus />
        </label>
        <label style={label}>Password
          <input style={input} type="password" value={password} onChange={e => setPassword(e.target.value)} />
        </label>
        {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <button type="submit" style={button(true)} disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
        <button type="button" onClick={() => setForgot(true)}
          style={{ ...button(false), border: 'none', marginLeft: 8 }}>Forgot password?</button>
      </form>
    </Shell>
  );
}

function StepRow({ step }) {
  // 'later': a texting step for a shop that started without texting. Waiting
  // on the shop, not on us, so it does not read as unfinished work.
  const colour = step.state === 'done' ? C.ok : step.state === 'partial' ? C.partial : step.state === 'later' ? C.muted : C.todo;
  const mark = step.state === 'done' ? '●' : step.state === 'partial' ? '◐' : step.state === 'later' ? '–' : '○';
  return (
    <div style={{ display: 'flex', gap: 10, padding: '6px 0', alignItems: 'flex-start' }}>
      <span style={{ color: colour, fontSize: 13, lineHeight: '18px' }}>{mark}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: step.state === 'done' ? C.text : C.sub }}>{step.label}</div>
        {step.detail && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{step.detail}</div>}
      </div>
    </div>
  );
}

// Editing a shop after it exists. The reason this is here at all: the carrier
// registration fields arrive days after the shop does, and without this the
// only way to enter them was hand-written SQL.
//
// Prefilled from what the shop already has, so an edit is a correction rather
// than a re-entry — and only changed fields are sent.
// The two ways a shop gets a texting number. The choice decides whether they can
// text in minutes or have to wait on a carrier, which is why it is asked early
// and shown on the checklist.
//
// Porting is deliberately not offered: it would move the shop's VOICE service to
// Twilio as well, and hosting gives them the same number with their phone
// service untouched. 'later' is not a way of getting a number; it records that
// the shop has not asked for one yet.
const PLANS = [
  ['new', 'New Twilio number',
    'Working immediately. Nothing needed from the shop — but no customer recognises the number, and the shop has to publish it everywhere.'],
  ['hosted', 'Hosted on their own line',
    'Twilio carries the texts, voice stays with their current carrier. Their known number, phone rings exactly as before. Usually the right answer for a shop that already advertises its number. Needs a signed LOA.'],
  // 17 Oct: a shop may start without texting. When they ask for it, switch
  // this to one of the two above, charge the setup fee, and collect the
  // carrier details the form skipped.
  ['later', 'Texting later',
    'The shop started without texting. Nothing to do until they ask; the texting steps below wait until then.'],
];

// Only the fields the chosen path needs. Asking a shop for a carrier account
// number when they are getting a fresh Twilio number is how a form teaches
// people to ignore it.
const PLAN_FIELDS = {
  new: [
    ['areaCode', 'Preferred area code', 'Twilio may not have one in every area code — a nearby one is usually fine.'],
    ['otherSmsProvider', 'Texting service they use today, if any',
      'Does not hold up a new number. They keep it until Spinboard texting is live, then cancel. Leave blank if none.'],
  ],
  hosted: [
    ['existingNumber', 'The number to host', 'Their existing published number. Voice keeps working through their current carrier.'],
    ['currentCarrier', 'Current phone carrier', ''],
    ['accountHolder', 'Account holder', 'The LOA has to be signed by whoever the carrier has on record — often not the person you are talking to.'],
    ['otherSmsProvider', 'Existing SMS provider, if any',
      'THE ONE THAT DELAYS THINGS. If another service already handles texts on this number, they must release it before hosting can be registered. Leave blank if none.'],
    ['loaSignedOn', 'LOA signed on', 'YYYY-MM-DD'],
    ['targetDate', 'Target go-live', 'YYYY-MM-DD'],
  ],
};

// Recording the shop's Twilio credentials. Separate from the rest of the form
// because it carries a secret: the auth token is never sent back by the server,
// so this always starts empty rather than showing a masked value that cannot be
// edited meaningfully.
function TwilioPanel({ shop, authFetch, onSaved }) {
  const [creds, setCreds] = useState({ accountSid: '', authToken: '', fromNumber: '', messagingServiceSid: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const set = (k) => (e) => { const v = e.target.value; setCreds(c => ({ ...c, [k]: v })); setError(''); setWarning(''); };

  const save = async () => {
    setBusy(true); setError(''); setWarning('');
    try {
      const r = await authFetch(`/api/admin/shops/${shop.id}/twilio`, { method: 'POST', body: JSON.stringify(creds) });
      const body = await r.json();
      if (!r.ok) throw new Error(body.error || 'The credentials were not saved.');
      setCreds({ accountSid: '', authToken: '', fromNumber: '', messagingServiceSid: '' });
      if (body.confirmedNumber === false) setWarning(body.note);
      else onSaved(`Twilio credentials saved for ${body.fromNumber}.`);
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  return (
    <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
      <div style={{ fontSize: 12, color: C.sub, marginBottom: 4 }}>
        Twilio credentials
        <Info tip="Verified against Twilio before they are saved. A wrong auth token otherwise fails as 'the shop's texts never arrive' weeks later, looking nothing like its cause." />
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
        {shop.twilio
          ? `Currently ${shop.twilio.fromNumber} on ${shop.twilio.accountSid}${shop.twilio.hasMessagingService ? ' with a messaging service' : ', no messaging service'}. Entering new values replaces them.`
          : 'Nothing recorded yet — this shop cannot send or receive texts.'}
        {' '}The auth token is never shown back, so this starts empty every time.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field labelText="Account SID" value={creds.accountSid} onChange={set('accountSid')} placeholder="AC…"
          tip="The SUBACCOUNT for this shop, not the parent Spinboard account." />
        <Field labelText="Auth token" value={creds.authToken} onChange={set('authToken')} placeholder="32 hex characters"
          tip="Stored once and never shown again. Verified with Twilio before saving." />
        <Field labelText="From number" value={creds.fromNumber} onChange={set('fromNumber')} placeholder="+19735551234"
          tip="One number per shop, always. Inbound texts are routed to a shop BY this number, so two shops cannot share one." />
        <Field labelText="Messaging service SID" value={creds.messagingServiceSid} onChange={set('messagingServiceSid')} placeholder="MG… (optional)"
          tip="The A2P campaign attaches to the messaging service, not to the number. Optional until registration is done." />
      </div>
      {warning && (
        <div style={{ fontSize: 12, color: C.partial, marginBottom: 10, lineHeight: 1.5 }}>
          Saved, with a caveat: {warning}
        </div>
      )}
      {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <button style={button(false)} onClick={save} disabled={busy}>
        {busy ? 'Verifying with Twilio…' : 'Verify and save credentials'}
      </button>
    </div>
  );
}

function EditShop({ shop, authFetch, onSaved, onCancel }) {
  const reg = shop.registration || {};
  const rep0 = reg.representative || {};
  const addr = shop.addressParts || {};
  const [form, setForm] = useState({
    name: shop.name || '', phone: shop.phone || '', hours: shop.hours || '',
    street: addr.street || '', city: addr.city || '', region: addr.region || '', postalCode: addr.postalCode || '',
    registration: {
      ...Object.fromEntries(REG_FIELDS.map(([k]) => [k, reg[k] || ''])),
      representative: Object.fromEntries(REP_FIELDS.map(([k]) => [k, rep0[k] || ''])),
    },
    numberPlan: { ...(shop.numberPlan || {}) },
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => { const v = e.target.value; setForm(f => ({ ...f, [k]: v })); setError(''); };
  const setPlan = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, numberPlan: { ...f.numberPlan, [k]: v } })); setError('');
  };
  const setReg = (k) => (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, registration: { ...f.registration, [k]: v } })); setError('');
  };
  const setRep = (k) => (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, registration: { ...f.registration, representative: { ...f.registration.representative, [k]: v } } }));
    setError('');
  };

  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await authFetch(`/api/admin/shops/${shop.id}`, { method: 'PATCH', body: JSON.stringify(form) });
      const body = await r.json();
      if (!r.ok) throw new Error(body.error || 'The change was not saved.');
      onSaved(body.changed && body.changed.length
        ? `Saved: ${body.changed.join(', ')}`
        : 'Nothing was different.');
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  return (
    <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Edit {shop.name}</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
        The booking slug is not editable here — it is printed on the shop's own website
        and in their emails, so changing it breaks links Spinboard cannot see.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
        <Field labelText="Shop name" value={form.name} onChange={set('name')}
          tip="Changes what customers see, and keeps the admin record in step with it." />
        <Field labelText="Shop phone" value={form.phone} onChange={set('phone')} />
        <Field labelText="Hours" value={form.hours} onChange={set('hours')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 0.6fr 0.8fr', gap: '0 16px' }}>
        <Field labelText="Street" value={form.street} onChange={set('street')} />
        <Field labelText="City" value={form.city} onChange={set('city')} />
        <Field labelText="State" value={form.region} onChange={set('region')} maxLength={2} />
        <Field labelText="ZIP" value={form.postalCode} onChange={set('postalCode')} />
      </div>
      <div style={{ fontSize: 12, color: C.sub, margin: '4px 0 8px' }}>
        Carrier registration <span style={{ color: C.muted }}>— usually what you are here to fill in</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        {REG_FIELDS.map(([key, labelText, hint, tip, list, options]) => (
          <Field key={key} labelText={labelText} hint={hint} tip={tip} list={list} options={options}
            value={form.registration[key]} onChange={setReg(key)} />
        ))}
      </div>
      <div style={{ fontSize: 12, color: C.sub, margin: '4px 0 8px' }}>Authorised representative</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
        {REP_FIELDS.map(([key, labelText, tip, list, options]) => (
          <Field key={key} labelText={labelText} tip={tip} list={list} options={options}
            value={form.registration.representative[key]} onChange={setRep(key)} />
        ))}
      </div>
      <div style={{ fontSize: 12, color: C.sub, margin: '14px 0 8px', borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
        How this shop gets a texting number
        <Info tip="This decides whether they can text customers in minutes or in weeks. It is the biggest single factor in an onboarding schedule." />
      </div>
      <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {PLANS.map(([value, labelText, why]) => (
          <label key={value} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="radio" name={`plan-${shop.id}`} checked={form.numberPlan.plan === value}
              onChange={() => setForm(f => ({ ...f, numberPlan: { ...f.numberPlan, plan: value } }))}
              style={{ accentColor: C.accent, marginTop: 3 }} />
            <span>
              <span style={{ fontSize: 13, color: C.text }}>{labelText}</span>
              <span style={{ display: 'block', fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{why}</span>
            </span>
          </label>
        ))}
      </div>
      {form.numberPlan.plan && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          {(PLAN_FIELDS[form.numberPlan.plan] || []).map(([key, labelText, tip]) => (
            <Field key={key} labelText={labelText} tip={tip}
              value={form.numberPlan[key] || ''} onChange={setPlan(key)} />
          ))}
        </div>
      )}
      {form.numberPlan.plan === 'hosted' && (
        <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: C.sub, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.numberPlan.loaSigned} onChange={setPlan('loaSigned')}
              style={{ accentColor: C.accent }} />
            LOA signed and in hand
          </label>
        </div>
      )}
      <Field labelText="Notes" value={form.numberPlan.notes || ''} onChange={setPlan('notes')}
        tip="Anything the next person needs to know — who you spoke to at the carrier, what they said, what you are waiting on."
        style={{ gridColumn: '1 / -1' }} />

      {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={button(true)} onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        <button style={button(false)} onClick={onCancel} disabled={busy}>Cancel</button>
      </div>

      <TwilioPanel shop={shop} authFetch={authFetch} onSaved={onSaved} />
    </div>
  );
}

// The shop's Spinboard subscription, from the Stripe Billing webhook. Before a
// signup: the link to send the owner, carrying this shop's reference so the
// checkout lands on this shop by itself.
function BillingLine({ shop, authFetch, onSaved }) {
  const b = shop.billing;
  const [copied, setCopied] = useState('');
  const [invoicing, setInvoicing] = useState(false);
  // The $75 texting setup fee by Stripe invoice, to the customer Stripe
  // already has for them (18 Oct). Stripe emails them a pay link; when they
  // pay, "setup fee paid" ticks itself.
  const sendFeeInvoice = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Email ${shop.name} a $75 invoice for the texting setup fee?\n\nStripe emails them a link to pay, due in 7 days. When they pay, "setup fee paid" ticks itself here and you can start their texting registration.`)) return;
    setInvoicing(true);
    try {
      const r = await authFetch(`/api/admin/shops/${shop.id}/setup-fee-invoice`, { method: 'POST', body: '{}' });
      const out = await r.json().catch(() => ({}));
      onSaved(r.ok
        ? (out.resent ? `The setup fee invoice already out to ${shop.name} was emailed again.` : `Setup fee invoice emailed to ${shop.name}. It ticks itself here when they pay.`)
        : (out.error || 'The invoice could not be sent.'));
    } catch (err) { onSaved(err.message); }
    setInvoicing(false);
  };
  // Complimentary (18 Oct): a shop Spinboard does not bill, such as
  // Manny's own. No subscription or setup fee is expected, and the owner's Getting
  // started guide stops asking them to subscribe.
  const setComplimentary = async (e, exempt) => {
    e.stopPropagation();
    const msg = exempt
      ? `Make ${shop.name} complimentary?\n\nSpinboard will not expect a subscription or setup fee from them, and their owner stops being asked to subscribe. Recorded in History. You can undo it here.`
      : `Start billing ${shop.name} again?\n\nTheir owner will be asked to start a subscription.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await authFetch(`/api/admin/shops/${shop.id}/complimentary`, { method: 'POST', body: JSON.stringify({ exempt }) });
      const out = await r.json().catch(() => ({}));
      onSaved(r.ok ? (exempt ? `${shop.name} is complimentary. Nothing is billed.` : `${shop.name} will be asked to subscribe.`) : (out.error || 'That did not work.'));
    } catch (err) { onSaved(err.message); }
  };
  if (!b) return null;
  const small = { ...button(false), fontSize: 11, padding: '3px 10px' };
  const date = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
  // Two links (18 Oct): the normal one, and the free-first-year one for shops
  // promised a year free. Both charge the $75 setup fee at checkout.
  const copy = async (e, which) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(which === 'year' ? b.freeYearSignupUrl : b.signupUrl);
      setCopied(which); setTimeout(() => setCopied(''), 2000);
    } catch (_) {}
  };
  return (
    <div style={{ fontSize: 12, color: C.sub, marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
      <span style={{ color: C.muted }}>Billing</span>
      {b.linked ? (
        <>
          <span style={{ color: ['trialing', 'active'].includes(b.status) ? C.ok : C.partial }}>{b.statusLabel || 'No subscription'}</span>
          {b.status === 'trialing' && b.trialEnd && <span>until {date(b.trialEnd)}</span>}
          <span>· setup fee {b.setupFeePaidAt ? `paid ${date(b.setupFeePaidAt)}` : 'not paid'}</span>
          {!b.setupFeePaidAt && authFetch && (
            <button onClick={sendFeeInvoice} disabled={invoicing} style={{ ...button(false), fontSize: 11, padding: '3px 10px' }}>
              {invoicing ? 'Sending…' : 'Send texting setup fee invoice ($75)'}
            </button>
          )}
          {b.lastPaymentFailedAt && <span style={{ color: C.danger }}>· a payment failed {date(b.lastPaymentFailedAt)}</span>}
        </>
      ) : b.exempt ? (
        <>
          <span style={{ color: C.ok }}>Complimentary, not billed</span>
          {authFetch && <button onClick={(e) => setComplimentary(e, false)} style={small}>Start billing</button>}
        </>
      ) : b.signupUrl ? (
        <>
          <span>No signup yet</span>
          <button onClick={(e) => copy(e, 'normal')} style={small}>{copied === 'normal' ? 'Copied' : 'Copy signup link'}</button>
          {b.freeYearSignupUrl && (
            <button onClick={(e) => copy(e, 'year')} style={small}
              title="Free for the first year, then the normal price. The $75 texting setup fee is still charged at checkout.">
              {copied === 'year' ? 'Copied' : 'Copy free-year signup link'}
            </button>
          )}
          {authFetch && <button onClick={(e) => setComplimentary(e, true)} style={small}>Make complimentary</button>}
        </>
      ) : (
        <span>No signup yet. Set BILLING_SIGNUP_LINK on the backend to get a link here.</span>
      )}
    </div>
  );
}

// Signups the webhook could not match to a shop (no shop reference on the
// checkout, and the email matched no single shop). Attach each by hand.
function UnmatchedSignups({ authFetch, shops, onLinked }) {
  const [signups, setSignups] = useState([]);
  const [pick, setPick] = useState({});
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await authFetch('/api/admin/billing/unmatched');
      const out = await r.json();
      if (r.ok) setSignups(out.signups || []);
    } catch (_) { /* a missing panel must not blank the console */ }
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);
  if (!signups.length) return null;
  const link = async (customerId) => {
    setError('');
    try {
      const r = await authFetch(`/api/admin/shops/${pick[customerId]}/billing`, { method: 'POST', body: JSON.stringify({ customerId }) });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.error || 'Could not attach it.');
      await load();
      onLinked();
    } catch (err) { setError(err.message); }
  };
  // A test or duplicate signup: hidden from this list, kept on record.
  const dismiss = async (su) => {
    if (!window.confirm(`Dismiss the signup from ${su.name || su.email || su.customerId}? It stays on record; it just leaves this list.`)) return;
    setError('');
    try {
      const r = await authFetch(`/api/admin/billing/unmatched/${encodeURIComponent(su.customerId)}/dismiss`, { method: 'POST' });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.error || 'Could not dismiss it.');
      await load();
      onLinked();
    } catch (err) { setError(err.message); }
  };
  return (
    <div style={{ ...box, borderColor: C.partial, marginBottom: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Signups to attach to a shop</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Paid through Stripe, but Spinboard could not tell which shop they belong to.</div>
      {signups.map(su => (
        <div key={su.customerId} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '6px 0', borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
          <span style={{ flex: '1 1 220px' }}>{su.name || 'No name'} · {su.email || su.customerId}</span>
          <select value={pick[su.customerId] || ''} onChange={e => setPick(p => ({ ...p, [su.customerId]: e.target.value }))}
            style={{ ...input, width: 'auto', marginTop: 0 }}>
            <option value="">Choose the shop…</option>
            {(shops || []).filter(s => !(s.billing && s.billing.linked)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button style={button(true)} disabled={!pick[su.customerId]} onClick={() => link(su.customerId)}>Attach</button>
          <button style={button(false)} onClick={() => dismiss(su)}>Dismiss</button>
        </div>
      ))}
      {error && <div style={{ color: C.danger, fontSize: 12, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// -- Intakes (17 Oct) -----------------------------------------------------------
// Invite a shop to the onboarding form, and see where each invited shop stands.

const INTAKE_STATUS = {
  invited: ['Invited, not started', C.muted],
  started: ['Filling it in', C.partial],
  sent_back: ['Sent back for changes', C.partial],
  submitted: ['Ready to review', C.accent],
  created: ['Shop created', C.ok],
};

function InviteShop({ authFetch, onInvited }) {
  const [form, setForm] = useState({ shopName: '', inviteName: '', inviteEmail: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const set = (k) => (e) => { const v = e.target.value; setForm(f => ({ ...f, [k]: v })); setError(''); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await authFetch('/api/admin/intakes', { method: 'POST', body: JSON.stringify(form) });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.error || 'Could not send the invite.');
      setDone(out);
      setForm({ shopName: '', inviteName: '', inviteEmail: '' });
      onInvited();
    } catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} style={{ ...box, marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Invite a shop</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
        Emails the owner a private link to the onboarding form. They fill it in at their own pace and start their subscription at the end.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <label style={label}>Shop name<input style={input} value={form.shopName} onChange={set('shopName')} /></label>
        <label style={label}>Owner name<input style={input} value={form.inviteName} onChange={set('inviteName')} /></label>
        <label style={label}>Owner email<input style={input} type="email" value={form.inviteEmail} onChange={set('inviteEmail')} /></label>
      </div>
      {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      {done && (
        <div style={{ fontSize: 12, color: done.emailed ? C.ok : C.partial, marginBottom: 10 }}>
          {done.emailed ? `Invite emailed to ${done.intake.inviteEmail}.` : 'The invite was created but the email did not send. Copy the link below and send it yourself.'}
          {' '}<span style={{ fontFamily: "'IBM Plex Mono', monospace", color: C.sub, wordBreak: 'break-all' }}>{done.intake.link}</span>
        </div>
      )}
      <button type="submit" style={button(true)} disabled={busy || !form.shopName || !form.inviteEmail}>{busy ? 'Sending…' : 'Send invite'}</button>
    </form>
  );
}

function IntakeCard({ intake, sections, authFetch, onChanged }) {
  const [open, setOpen] = useState(intake.status === 'submitted');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const [override, setOverride] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [label0, colour] = INTAKE_STATUS[intake.status] || [intake.status, C.muted];
  const prog = (intake.progress || []).filter(p => !p.skipped);
  const done = prog.filter(p => p.complete).length;
  const date = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
  const post = async (path, body, ok) => {
    setSending(true); setMsg('');
    try {
      const r = await authFetch(path, { method: 'POST', body: JSON.stringify(body || {}) });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.error || 'That did not work.');
      setMsg(ok(out)); setNote(''); onChanged();
    } catch (err) { setMsg(err.message); }
    setSending(false);
  };
  const copy = async () => { try { await navigator.clipboard.writeText(intake.link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) {} };
  return (
    <div style={{ ...box, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => setOpen(v => !v)}>
        <div style={{ flex: '1 1 200px' }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{intake.answers.name || intake.shopName}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            {intake.inviteName ? `${intake.inviteName} · ` : ''}{intake.inviteEmail} · invited {date(intake.createdAt)}{intake.invitedBy ? ` by ${intake.invitedBy}` : ''}
          </div>
        </div>
        <div style={{ fontSize: 12, color: C.sub }}>
          {intake.status === 'created' ? `shop ${intake.shopId}` : `${done}/${prog.length} sections`} · updated {date(intake.updatedAt)}
        </div>
        <div style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, border: `1px solid ${colour}`, color: colour }}>
          {intake.archivedAt ? `archived ${date(intake.archivedAt)}` : label0}
        </div>
        {/* Tidying the list. Archiving stops an unfinished invite's link
            working; Restore brings it back. Nothing is deleted. */}
        <button style={{ ...button(false), fontSize: 11, padding: '4px 10px' }} disabled={sending}
          onClick={(e) => {
            e.stopPropagation();
            if (intake.archivedAt) return post(`/api/admin/intakes/${intake.id}/restore`, {}, () => 'Restored.');
            const linkNote = intake.status === 'created' ? '' : ' Its form link stops working until you restore it.';
            if (!window.confirm(`Archive the invite for ${intake.answers.name || intake.shopName}?${linkNote}`)) return;
            post(`/api/admin/intakes/${intake.id}/archive`, {}, () => 'Archived.');
          }}>{intake.archivedAt ? 'Restore' : 'Archive'}</button>
        <span style={{ color: C.muted, fontSize: 11 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
          {intake.status === 'sent_back' && intake.note && (
            <div style={{ fontSize: 12, color: C.partial, marginBottom: 10 }}>Your note: {intake.note}</div>
          )}
          {intake.terms && (
            <div style={{ fontSize: 12, color: C.sub, marginBottom: 10 }}>
              Agreement {intake.terms.version} accepted by {intake.terms.name} on {new Date(intake.terms.at).toLocaleString()}
            </div>
          )}
          {(sections || []).map(sec => (
            <div key={sec.key} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted, marginBottom: 4 }}>{sec.title}</div>
              {sec.skipIf && intake.answers[sec.skipIf[0]] === sec.skipIf[1] && (
                <div style={{ fontSize: 13, color: C.sub }}>Skipped: the shop chose to set texting up later.</div>
              )}
              {!(sec.skipIf && intake.answers[sec.skipIf[0]] === sec.skipIf[1]) && sec.fields.map(f => {
                const v = intake.answers[f.key];
                const problem = intake.problems && intake.problems[f.key];
                if (!v && !problem) return null;
                return (
                  <div key={f.key} style={{ display: 'flex', gap: 10, fontSize: 13, padding: '2px 0' }}>
                    <span style={{ flex: '0 0 220px', color: C.sub }}>{f.label}</span>
                    <span style={{ color: problem ? C.partial : C.text, wordBreak: 'break-word' }}>{v || '—'}{problem ? `  (${problem})` : ''}</span>
                  </div>
                );
              })}
            </div>
          ))}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <button style={button(false)} onClick={copy}>{copied ? 'Copied' : 'Copy form link'}</button>
            {intake.status !== 'created' && (
              <button style={button(false)} disabled={sending} onClick={() => post(`/api/admin/intakes/${intake.id}/resend`, {}, (o) => (o.emailed ? 'Link emailed again, good for 30 more days.' : 'Link extended, but the email did not send.'))}>Email the link again</button>
            )}
          </div>
          {intake.status === 'submitted' && (
            <div style={{ marginTop: 14, padding: 14, border: `1px solid ${C.accent}`, borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Create the shop</div>
              <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.6, marginBottom: 10 }}>
                Creates the shop, owner login and settings from these answers; allows their Lightspeed account;
                emails the owner a link to choose their password; and attaches their subscription.
              </div>
              {/* No shop until the subscription is in (18 Oct). The
                  subscription is the free trial, so a real trial loses
                  nothing; the override is for a shop Manny lets in early. */}
              {intake.subscribed ? (
                <div style={{ fontSize: 12, color: C.ok, marginBottom: 10 }}>● Subscription started.</div>
              ) : (
                <div style={{ fontSize: 12, color: C.partial, lineHeight: 1.6, marginBottom: 10 }}>
                  Waiting for their subscription. Create shop unlocks once it comes through.
                  {intake.signupUrl ? (
                    <> <button style={{ ...button(false), fontSize: 11, padding: '3px 9px', marginLeft: 6 }}
                      onClick={async () => { try { await navigator.clipboard.writeText(intake.signupUrl); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); } catch (_) {} }}>
                      {linkCopied ? 'Copied' : 'Copy their signup link'}</button></>
                  ) : ' No signup link is set up for their choice yet.'}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, color: C.sub, cursor: 'pointer' }}>
                    <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)} />
                    Create without a subscription (recorded in History)
                  </label>
                </div>
              )}
              <button style={{ ...button(true), opacity: (intake.problems && Object.keys(intake.problems).length > 0) || (!intake.subscribed && !override) ? 0.4 : 1 }}
                disabled={sending || (intake.problems && Object.keys(intake.problems).length > 0) || (!intake.subscribed && !override)}
                onClick={() => {
                  const extra = intake.subscribed ? '' : ' They have NOT started their subscription.';
                  if (!window.confirm(`Create ${intake.answers.name || intake.shopName} now? The owner gets their welcome email straight away.${extra}`)) return;
                  post(`/api/admin/intakes/${intake.id}/create-shop`, intake.subscribed ? {} : { withoutSubscription: true }, (o) => [
                    `Shop ${o.shopId} created.`,
                    o.allowlisted ? `Lightspeed account ${o.allowlisted} allowed.` : null,
                    o.emailed ? 'Welcome email sent.' : null,
                    o.billing === 'attached' ? 'Subscription attached.' : 'No subscription yet: it attaches itself when they sign up.',
                    ...(o.problems || []),
                  ].filter(Boolean).join(' '));
                }}>Create shop</button>
              {intake.problems && Object.keys(intake.problems).length > 0 && (
                <div style={{ fontSize: 12, color: C.partial, marginTop: 6 }}>Some answers need fixing first. Send it back with a note.</div>
              )}
            </div>
          )}
          {intake.status === 'submitted' && (
            <div style={{ marginTop: 12 }}>
              <label style={label}>Send back with a note (their answers stay filled in)
                <textarea style={{ ...input, minHeight: 60 }} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="e.g. The legal name needs to match your IRS letter exactly." />
              </label>
              <button style={button(false)} disabled={sending || !note.trim()} onClick={() => post(`/api/admin/intakes/${intake.id}/send-back`, { note }, (o) => (o.emailed ? 'Sent back; they have the note by email.' : 'Sent back, but the email did not send.'))}>Send back</button>
            </div>
          )}
          {msg && <div style={{ fontSize: 12, color: C.sub, marginTop: 8 }}>{msg}</div>}
        </div>
      )}
    </div>
  );
}

function Intakes({ authFetch, onShopCreated }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await authFetch('/api/admin/intakes');
      const out = await r.json();
      if (r.ok) setData(out);
    } catch (_) { /* the panel failing must not blank the console */ }
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);
  const [showArchived, setShowArchived] = useState(false);
  const intakes = (data && data.intakes) || [];
  const archived = intakes.filter(i => i.archivedAt);
  const changed = () => { load(); if (onShopCreated) onShopCreated(); };
  return (
    <div style={{ marginBottom: 20 }}>
      <InviteShop authFetch={authFetch} onInvited={load} />
      {/* Created intakes stay a week, so the result of Create shop is still on screen. */}
      {intakes.filter(i => !i.archivedAt && (i.status !== 'created' || Date.now() - new Date(i.updatedAt).getTime() < 7 * 864e5)).map(i => (
        <IntakeCard key={i.id} intake={i} sections={data.sections} authFetch={authFetch} onChanged={changed} />
      ))}
      {archived.length > 0 && (
        <button onClick={() => setShowArchived(v => !v)}
          style={{ background: 'none', border: 'none', padding: '4px 0', color: C.sub, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          {showArchived ? '▲ Hide archived invites' : `▼ Show archived invites (${archived.length})`}
        </button>
      )}
      {showArchived && archived.map(i => (
        <div key={i.id} style={{ opacity: 0.7 }}>
          <IntakeCard intake={i} sections={data.sections} authFetch={authFetch} onChanged={changed} />
        </div>
      ))}
    </div>
  );
}

// Carrier registration, run from here (22 Oct). Each button runs the same
// server/scripts/twilio-*.js step as before, on the server, for this shop
// only. The server enforces the order and the setup fee; the brand and the
// campaign cost money, so they show a review (dry run) first and submit only
// after a typed confirmation. helpers/registrationRunner.js.
function RegistrationPanel({ shop, authFetch, onSaved }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { label, ok, dryRun, output }
  const [showOut, setShowOut] = useState(false);
  const [reviewed, setReviewed] = useState({}); // step key -> true once its dry run passed

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`/api/admin/shops/${shop.id}/registration`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not read the registration.');
      setState(d);
    } catch (e) { setError(e.message); }
  }, [authFetch, shop.id]);
  useEffect(() => { if (!shop.textingLater) load(); }, [load, shop.textingLater]);
  if (shop.textingLater) return null;

  const run = async (st, confirm) => {
    if (confirm && !window.confirm(`Submit the ${st.label.toLowerCase()} for ${shop.name}? ${st.key === 'brand' ? 'This charges about $59 to Spinboard\'s Twilio account and cannot be refunded.' : 'This starts a campaign at about $10 a month and sends it to the carriers for review.'}`)) return;
    setBusy(st.key); setError(''); setResult(null); setShowOut(false);
    try {
      const r = await authFetch(`/api/admin/shops/${shop.id}/registration/${st.key}`, { method: 'POST', body: JSON.stringify({ confirm: !!confirm }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'The step did not run.');
      setState({ steps: d.steps, goLive: d.goLive });
      setResult({ label: st.label, ok: d.ok, dryRun: d.dryRun, output: d.output, recorded: d.recorded });
      if (d.dryRun && d.ok) setReviewed(v => ({ ...v, [st.key]: true }));
      if (!d.ok) setShowOut(true);
      if (d.recorded) onSaved(`${st.label} recorded for ${shop.name}.`);
    } catch (e) { setError(e.message); }
    setBusy('');
  };

  const goLive = async () => {
    if (!window.confirm(`Make the approved campaign live for ${shop.name}? Its texts will send through the messaging service from now on.`)) return;
    setBusy('go-live'); setError(''); setResult(null);
    try {
      const r = await authFetch(`/api/admin/shops/${shop.id}/registration/go-live`, { method: 'POST', body: '{}' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Nothing was changed.');
      setState({ steps: d.steps, goLive: d.goLive });
      setResult({ label: 'Go live', ok: true, output: (d.notes || []).join('\n') });
      setShowOut(true);
      onSaved(`${shop.name} is texting under its approved campaign.`);
    } catch (e) { setError(e.message); }
    setBusy('');
  };

  const small = { ...button(false), fontSize: 11, padding: '4px 10px' };
  return (
    <div style={{ margin: '4px 0 8px 23px', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8 }}>
      <div style={{ fontSize: 12, color: C.sub, marginBottom: 6 }}>
        Carrier registration
        <Info tip="Each button runs the same registration script as before, on the server, for this shop only. Steps unlock in order once the setup fee is paid. The brand and the campaign show a review first and only submit after you confirm." />
      </div>
      {!state && !error && <div style={{ fontSize: 11, color: C.muted }}>Reading…</div>}
      {state && state.steps.map((st, i) => (
        <div key={st.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '5px 0' }}>
          <span style={{ color: st.done ? C.ok : C.todo, fontSize: 12, lineHeight: '18px' }}>{st.done ? '●' : '○'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, color: st.done ? C.text : C.sub }}>{i + 1}. {st.label}{st.done && st.sid ? <span style={{ color: C.muted, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>  {st.sid}</span> : null}</div>
            {!st.done && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{st.blocked || st.what}</div>}
          </div>
          {!st.done && st.canRun && (
            st.billable ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={small} disabled={!!busy} onClick={() => run(st, false)}>{busy === st.key ? 'Running…' : 'Review'}</button>
                <button style={{ ...small, color: reviewed[st.key] ? C.accent : C.muted, borderColor: reviewed[st.key] ? C.accent : C.border }}
                  disabled={!!busy || !reviewed[st.key]} title={reviewed[st.key] ? '' : 'Review it first'}
                  onClick={() => run(st, true)}>Submit</button>
              </div>
            ) : (
              <button style={small} disabled={!!busy} onClick={() => run(st, false)}>{busy === st.key ? 'Running…' : 'Run'}</button>
            )
          )}
        </div>
      ))}
      {state && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
          <div style={{ flex: 1, fontSize: 11, color: C.muted }}>
            {state.goLive.live && !state.goLive.canRun
              ? <>Live: texts send through messaging service <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{state.goLive.liveService}</span>.</>
              : 'Go live: once Twilio shows the campaign approved, attach the number and switch the shop\'s texts to the messaging service. Refused if the campaign is not approved.'}
          </div>
          {state.goLive.canRun && <button style={small} disabled={!!busy} onClick={goLive}>{busy === 'go-live' ? 'Checking…' : 'Go live'}</button>}
        </div>
      )}
      {error && <div style={{ color: C.danger, fontSize: 12, marginTop: 8 }}>{error}</div>}
      {result && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: result.ok ? C.ok : C.danger }}>
            {result.label}: {result.ok ? (result.dryRun ? 'review passed. Read the output, then Submit.' : result.recorded ? `done, ${result.recorded}.` : 'done.') : 'failed. Nothing was recorded; the output says why.'}
            {' '}<button style={{ background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 11, textDecoration: 'underline', padding: 0 }} onClick={() => setShowOut(v => !v)}>{showOut ? 'Hide output' : 'Show output'}</button>
          </div>
          {showOut && (
            <pre style={{ marginTop: 6, maxHeight: 280, overflow: 'auto', background: '#0c131e', border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, fontSize: 11, color: C.sub, whiteSpace: 'pre-wrap' }}>{result.output}</pre>
          )}
        </div>
      )}
    </div>
  );
}

// Spinboard's per-shop feature switches (25 Oct). Customer feedback is on for
// every shop until switched off here; off hides it from the shop entirely.
function FeaturesLine({ shop, authFetch, onSaved }) {
  const on = !shop.features || shop.features.customerFeedback !== false;
  const flip = async (e) => {
    e.stopPropagation();
    if (on && !window.confirm(`Turn off customer feedback for ${shop.name}?\n\nTheir Settings card and Reviews screen disappear, no new customers are asked, and any requests waiting to go are dropped. Their settings and past answers are kept. Recorded in History; you can turn it back on here.`)) return;
    const r = await authFetch(`/api/admin/shops/${shop.id}/features/customer-feedback`, { method: 'POST', body: JSON.stringify({ enabled: !on }) });
    const out = await r.json().catch(() => ({}));
    onSaved(r.ok ? `Customer feedback is ${!on ? 'on' : 'off'} for ${shop.name}.` : (out.error || 'That did not work.'));
  };
  return (
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
      Customer feedback: <span style={{ color: on ? C.ok : C.partial }}>{on ? 'on' : 'off'}</span>
      <button onClick={flip} style={{ ...button(false), fontSize: 11, padding: '2px 8px' }}>{on ? 'Turn off' : 'Turn on'}</button>
    </div>
  );
}

// A shop's website (3 Nov): open its page builder, start a plain site, download
// the whole site as a file, or copy a website in (from another shop here, or
// from a file downloaded from another Spinboard: staging to production).
function WebsiteLine({ shop, shops, authFetch, onSaved }) {
  const [copying, setCopying] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const has = shop.website && shop.website.exists;
  const on = !!(shop.features && shop.features.website);
  const stop = (e) => e.stopPropagation();
  const call = async (what, path, opts) => {
    setBusy(what); setErr('');
    try {
      const r = await authFetch(path, opts);
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.error || 'That did not work.');
      return out;
    } catch (e) { setErr(e.message); return null; } finally { setBusy(''); }
  };
  const openBuilder = async (e) => {
    stop(e);
    const tab = window.open('', '_blank');
    const out = await call('open', `/api/admin/shops/${shop.id}/website/builder`, { method: 'POST', body: '{}' });
    if (out && out.url) { if (tab) tab.location.href = out.url; else window.location.href = out.url; } else if (tab) tab.close();
  };
  // Spinboard's switch (3 Nov): whether the shop sees Settings › Website.
  const flip = async (e) => {
    stop(e);
    if (!on && !window.confirm(`Show Settings › Website to ${shop.name}?\n\nThey can then start, edit and publish their website themselves. Recorded in History; you can hide it again here.`)) return;
    const out = await call('flip', `/api/admin/shops/${shop.id}/features/website`, { method: 'POST', body: JSON.stringify({ enabled: !on }) });
    if (out) onSaved(`Settings › Website is ${!on ? 'shown to' : 'hidden from'} ${shop.name}.${on ? ' Their website, if published, stays up.' : ''}`);
  };
  const start = async (e) => {
    stop(e);
    if (!window.confirm(`Start a plain website for ${shop.name}?\n\nA home page and a Visit us page, with the shop details from their Settings › Shop. Nothing is public until a domain is connected.`)) return;
    const out = await call('start', `/api/admin/shops/${shop.id}/website/start`, { method: 'POST', body: '{}' });
    if (out) onSaved(`${shop.name} has a plain website. Open the builder to make it theirs.`);
  };
  const download = async (e) => {
    stop(e);
    setBusy('file'); setErr('');
    try {
      const r = await authFetch(`/api/admin/shops/${shop.id}/website/file`);
      if (!r.ok) { const out = await r.json().catch(() => ({})); throw new Error(out.error || 'That did not work.'); }
      const name = (r.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a'); a.href = url; a.download = name ? name[1] : 'website.json'; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (x) { setErr(x.message); }
    setBusy('');
  };
  const small = { ...button(false), fontSize: 11, padding: '2px 8px' };
  return (
    <div onClick={stop} style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        Website: <span style={{ color: has ? C.ok : C.muted }}>{has ? (shop.website.liveDomain ? `live at ${shop.website.liveDomain}` : 'in Spinboard, not on a domain yet') : 'none yet'}</span>
        <span>· shop {on ? 'sees' : 'does not see'} it in Settings</span>
        <button style={small} disabled={!!busy} onClick={flip}>{on ? 'Hide from shop' : 'Show to shop'}</button>
        {has && <button style={small} disabled={!!busy} onClick={openBuilder}>{busy === 'open' ? 'Opening…' : 'Open builder'}</button>}
        {!has && <button style={small} disabled={!!busy} onClick={start}>Start a plain one</button>}
        {has && <button style={small} disabled={!!busy} onClick={download}>{busy === 'file' ? 'Preparing…' : 'Download website file'}</button>}
        <button style={small} disabled={!!busy} onClick={() => setCopying((v) => !v)}>{copying ? 'Close copy' : 'Copy a website in…'}</button>
      </div>
      {err && <div style={{ color: C.danger, marginTop: 4 }}>{err}</div>}
      {copying && <CopyWebsite shop={shop} shops={shops} authFetch={authFetch} onDone={(msg) => { setCopying(false); onSaved(msg); }} />}
    </div>
  );
}

function CopyWebsite({ shop, shops, authFetch, onDone }) {
  const [source, setSource] = useState(null); // { fromShop } | { file, fileName }
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const others = (shops || []).filter((s) => s.id !== shop.id && s.website && s.website.exists);
  const post = async (body) => {
    const r = await authFetch(`/api/admin/shops/${shop.id}/website/copy`, { method: 'POST', body: JSON.stringify(body) });
    const out = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(out.error || (r.status === 413 ? 'That file is too big.' : 'That did not work.'));
    return out;
  };
  const look = async (src) => {
    setSource(src); setPlan(null); setErr(''); setBusy(true);
    try { setPlan((await post({ ...(src.fromShop ? { fromShop: src.fromShop } : { file: src.file }) })).plan); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const pickFile = async (f) => {
    if (!f) return;
    try { look({ file: JSON.parse(await f.text()), fileName: f.name }); } catch (_) { setErr('That file is not a Spinboard website file.'); }
  };
  const copy = async () => {
    if (plan.warnings.length && !window.confirm(`Copy ${plan.from.name || 'this website'} into ${shop.name}?\n\n${plan.warnings.join('\n\n')}`)) return;
    setBusy(true); setErr('');
    try {
      const out = await post({ ...(source.fromShop ? { fromShop: source.fromShop } : { file: source.file }), confirm: true });
      onDone(`Copied into ${shop.name}: ${out.pages} pages (${out.published} published) and ${out.photos} photos${out.missing ? `; ${out.missing} photo${out.missing === 1 ? '' : 's'} could not be copied` : ''}. The website shows it within two minutes.`);
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  const sel = { ...input, width: 'auto', marginTop: 0, padding: '4px 8px', fontSize: 12 };
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginTop: 8, color: C.sub }}>
      <div style={{ marginBottom: 8 }}>Copy a whole website into {shop.name}: pages, menus, footer, colors and fonts, and photos. Bikes, sign-ups and domains are not copied.</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select style={sel} value={source && source.fromShop ? String(source.fromShop) : ''} disabled={busy} onChange={(e) => e.target.value && look({ fromShop: Number(e.target.value) })}>
          <option value="">From a shop here…</option>
          {others.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <span>or</span>
        <label style={{ ...button(false), fontSize: 11, padding: '4px 10px', display: 'inline-block' }}>
          {source && source.fileName ? source.fileName : 'From a website file…'}
          <input type="file" accept=".json,application/json" style={{ display: 'none' }} disabled={busy} onChange={(e) => pickFile(e.target.files && e.target.files[0])} />
        </label>
      </div>
      <div style={{ fontSize: 11, marginTop: 6 }}>A website file comes from "Download website file" in another Spinboard's Console (staging, for example). Its photos are fetched from there, so that Spinboard must be running.</div>
      {busy && !plan && <div style={{ marginTop: 8 }}>Reading…</div>}
      {plan && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: C.text }}>From {plan.from.name || plan.from.slug}{plan.from.api ? ` (${plan.from.api.replace(/^https?:\/\//, '')})` : ''}: {plan.pages} pages, {plan.published} of them published, and {plan.photos} photos.</div>
          {plan.sameShop && <div style={{ marginTop: 4 }}>Same shop: its details and old-address redirects come with it.</div>}
          {plan.warnings.map((w, i) => <div key={i} style={{ color: C.partial, marginTop: 4 }}>{w}</div>)}
          <button style={{ ...button(true), marginTop: 10, fontSize: 12, padding: '6px 14px' }} disabled={busy} onClick={copy}>{busy ? 'Copying… (photos take a moment)' : `Copy into ${shop.name}`}</button>
        </div>
      )}
      {err && <div style={{ color: C.danger, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

function ShopCard({ shop, shops, authFetch, onSaved }) {
  const [open, setOpen] = useState(!shop.live && !shop.hidden);
  const [editing, setEditing] = useState(false);
  // Hiding only tidies this list: the shop keeps working and its staff keep
  // signing in.
  const toggleHidden = async (e) => {
    e.stopPropagation();
    if (!shop.hidden && !window.confirm(`Hide ${shop.name} from this list? The shop keeps working; this only tidies the console. You can show it again any time.`)) return;
    const r = await authFetch(`/api/admin/shops/${shop.id}/${shop.hidden ? 'unhide' : 'hide'}`, { method: 'POST', body: '{}' });
    const out = await r.json().catch(() => ({}));
    onSaved(r.ok ? (shop.hidden ? `${shop.name} is back in the list.` : `${shop.name} hidden. "Show hidden shops" at the bottom brings it back.`) : (out.error || 'That did not work.'));
  };
  return (
    <div style={{ ...box, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
        onClick={() => setOpen(v => !v)}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{shop.name}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            shop {shop.id} · <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{shop.bookingPath}</span>
          </div>
          {/* Only when the two names disagree — the shop was renamed in
              Settings after it was created, and this record is stale. */}
          {shop.nameChanged && (
            <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
              created as “{shop.createdAs}”
            </div>
          )}
          <BillingLine shop={shop} authFetch={authFetch} onSaved={onSaved} />
          <FeaturesLine shop={shop} authFetch={authFetch} onSaved={onSaved} />
          <WebsiteLine shop={shop} shops={shops} authFetch={authFetch} onSaved={onSaved} />
        </div>
        <div style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12,
          background: shop.live ? '#123a24' : '#2a2416', color: shop.live ? C.ok : C.partial }}>
          {shop.live ? 'live' : `${shop.done}/${shop.total} done`}
        </div>
        <button style={{ ...button(false), fontSize: 11, padding: '4px 10px' }}
          onClick={(e) => { e.stopPropagation(); setEditing(v => !v); setOpen(true); }}>
          {editing ? 'Close' : 'Edit'}
        </button>
        <button style={{ ...button(false), fontSize: 11, padding: '4px 10px' }} onClick={toggleHidden}>
          {shop.hidden ? 'Unhide' : 'Hide'}
        </button>
        <span style={{ color: C.muted, fontSize: 11 }}>{open ? '▲' : '▼'}</span>
      </div>
      {shop.error && <div style={{ color: C.danger, fontSize: 12, marginTop: 10 }}>{shop.error}</div>}
      {open && !editing && !!shop.steps.length && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
          {/* Grouped by the runbook's stages (22 Oct); the server sends them in order. */}
          {shop.steps.map((s, i) => (
            <React.Fragment key={s.key}>
              {s.stage && (i === 0 || shop.steps[i - 1].stage !== s.stage) && (
                <div style={{ fontSize: 11, fontWeight: 600, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: i === 0 ? 4 : 14, marginBottom: 2 }}>
                  {s.stage}
                </div>
              )}
              <StepRow step={s} />
              {s.key === 'a2p' && <RegistrationPanel shop={shop} authFetch={authFetch} onSaved={onSaved} />}
            </React.Fragment>
          ))}
        </div>
      )}
      {editing && (
        <EditShop shop={shop} authFetch={authFetch}
          onCancel={() => setEditing(false)}
          onSaved={(msg) => { setEditing(false); onSaved(msg); }} />
      )}
    </div>
  );
}

const EMPTY = {
  name: '', slug: '', ownerUsername: '', ownerEmail: '', phone: '', hours: '', employeeId: '',
  // Structured, not one line: carrier registration takes these as separate
  // fields and matches them against the business filing.
  street: '', city: '', region: '', postalCode: '',
  registration: {
    legalName: '', ein: '', website: '', businessType: '', companyType: '', businessIndustry: '',
    displayBrandName: '', shopContactName: '',
    privacyPolicyUrl: '', termsAndConditionsUrl: '', optInUrl: '',
    representative: { firstName: '', lastName: '', email: '', phone: '', businessTitle: '', jobPosition: '' },
  },
};

// The carrier-registration half of the form. Everything here is optional at
// creation and every one of them is required before an A2P filing — which
// takes days and gets rejected over exactly this data. Gathering it while
// you are still talking to the shop costs a minute.
// Field names match server/scripts/twilio-*.js exactly. The suggestion lists
// are the values that got a real campaign approved — offered, not enforced,
// because the accepted set is Twilio's and it changes.
const REG_FIELDS = [
  ['legalName', 'Legal business name', 'as registered',
    'The exact name on the business tax registration, usually ending in LLC or Inc. NOT the trading name. A mismatch with the EIN is the most common reason a carrier rejects a registration.'],
  ['ein', 'EIN', '12-3456789',
    'The federal employer ID number. The owner will have it on their SS-4 confirmation letter, or their accountant will. Sole proprietors without one cannot register a campaign.'],
  ['website', 'Website', 'https://…',
    'The carrier visits this. It must load, and it must obviously belong to the same business as the legal name above.'],
  ['businessType', 'Business type', 'legal form',
    'The legal form of the business. Limited Liability Corporation is the usual answer for an independent bike shop.', 'dl-businesstype',
    ['Limited Liability Corporation', 'Corporation', 'Partnership', 'Sole Proprietorship', 'Non-profit Corporation']],
  ['companyType', 'Company type', '',
    'Whether the company is privately held or publicly traded. Almost always private.', 'dl-companytype',
    ['private', 'public']],
  ['businessIndustry', 'Industry', '',
    'Twilio\'s industry category. A bike shop is RETAIL.', 'dl-industry',
    ['RETAIL', 'PROFESSIONAL', 'HOSPITALITY', 'MANUFACTURING', 'TRANSPORTATION', 'HEALTHCARE', 'EDUCATION', 'NGO', 'TECHNOLOGY']],
  ['displayBrandName', 'Brand name customers see', '',
    'The name shown in the campaign registration — what a customer would recognise, usually the trading name rather than the legal one.'],
  ['shopContactName', 'Contact name for the campaign', '',
    'Who the carrier contacts about this campaign. Usually the owner.'],
  ['privacyPolicyUrl', 'Privacy policy URL', 'https://…',
    'Must be publicly reachable and must mention SMS. If the shop has no privacy policy, this is the thing to sort out first — the carrier will check.'],
  ['termsAndConditionsUrl', 'Terms and conditions URL', 'https://…',
    'Publicly reachable terms covering the messaging. Same rule as the privacy policy.'],
  ['optInUrl', 'Opt-in evidence URL', '',
    'A page showing HOW customers agree to be texted — the booking form, or a page describing it. The carrier wants proof that consent is collected, not just claimed.'],
];
const REP_FIELDS = [
  ['firstName', 'First name', 'A real person at the business, not a role account. The carrier may contact them.'],
  ['lastName', 'Last name', ''],
  ['email', 'Email', 'Reachable, and at the business\'s own domain if they have one — a generic address weakens the filing.'],
  ['phone', 'Phone', 'Include the area code. This is the person, not the shop line.'],
  ['businessTitle', 'Business title', 'What they are called at the business, e.g. Owner.'],
  ['jobPosition', 'Job position', 'Twilio\'s category for the title above. CEO is the usual answer for an owner-operator.', 'dl-jobposition',
    ['CEO', 'CFO', 'Director', 'GM', 'VP', 'General Counsel', 'Other']],
];

function CreateShop({ onCreated, authFetch }) {
  const [form, setForm] = useState(EMPTY);
  const [checked, setChecked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const [showReg, setShowReg] = useState(false);

  const touched = () => { setChecked(null); setError(''); };
  const set = (k) => (e) => { const v = e.target.value; setForm(f => ({ ...f, [k]: v })); touched(); };
  const setReg = (k) => (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, registration: { ...f.registration, [k]: v } }));
    touched();
  };
  const setRep = (k) => (e) => {
    const v = e.target.value;
    setForm(f => ({
      ...f,
      registration: { ...f.registration, representative: { ...f.registration.representative, [k]: v } },
    }));
    touched();
  };

  const check = async () => {
    setBusy(true); setError('');
    try {
      const r = await authFetch('/api/admin/shops/check', { method: 'POST', body: JSON.stringify(form) });
      setChecked(await r.json());
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  const create = async () => {
    setBusy(true); setError('');
    try {
      const r = await authFetch('/api/admin/shops', { method: 'POST', body: JSON.stringify(form) });
      const body = await r.json();
      if (!r.ok) throw new Error(body.error || 'The shop was not created.');
      setCreated(body);
      setForm(EMPTY); setChecked(null);
      onCreated();
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  // The password is shown once and cannot be recovered, so this panel stays put
  // until it is dismissed deliberately rather than disappearing on the next
  // render.
  if (created) {
    return (
      <div style={{ ...box, borderColor: C.ok }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{created.shop.name} created</div>
        <div style={{ fontSize: 12, color: C.sub, marginBottom: 12 }}>
          Booking page: <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{created.shop.bookingPath}</span>
        </div>
        {created.ownerPassword && (
          <div style={{ background: '#0c131e', border: `1px solid ${C.border}`, borderRadius: 7, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: C.sub, marginBottom: 4 }}>Owner's password — shown once, stored only as a hash</div>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 15 }}>{created.ownerPassword}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
              Copy it now. Have them change it after their first sign-in.
            </div>
          </div>
        )}
        <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.6 }}>
          Next: the owner connects Lightspeed and Stripe themselves, and Twilio needs a
          number and A2P registration — which takes days. The checklist below tracks all of it.
        </div>
        <button style={{ ...button(false), marginTop: 12 }} onClick={() => setCreated(null)}>Done</button>
      </div>
    );
  }

  return (
    <div style={box}>
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Add a shop</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field labelText="Shop name" hint="what customers see" value={form.name} onChange={set('name')}
          placeholder="Spoke House Bicycles"
          tip="Shown on the booking page, in emails and in texts. The owner can change it later in Settings without affecting anything else." />
        <Field labelText="Booking slug" hint="hard to change later" value={form.slug} onChange={set('slug')}
          placeholder="spokehouse"
          tip="Becomes their booking address: /booking/<slug>. It gets printed on their website and in their emails, so changing it later breaks those links. Lowercase letters, numbers and hyphens only." />
        <Field labelText="Owner username" value={form.ownerUsername} onChange={set('ownerUsername')}
          placeholder="maria"
          tip="Unique across EVERY shop on Spinboard, not just this one — two shops cannot both have a 'manager'. Pick something specific." />
        <Field labelText="Owner email" hint="resets go here" value={form.ownerEmail} onChange={set('ownerEmail')}
          placeholder="owner@theirshop.com"
          tip="Must be a mailbox they actually read: the password reset link is the only way back in. Also unique across every shop." />
        <Field labelText="Shop phone" value={form.phone} onChange={set('phone')}
          placeholder="(555) 010-0100"
          tip="Shown to customers in emails and on the booking page. This is NOT the texting number — that one comes from Twilio later." />
        <Field labelText="Hours" hint="as customers should read them" value={form.hours} onChange={set('hours')}
          placeholder="Tue–Sat 10–6"
          tip="Free text, shown to customers exactly as typed, so 'Sat by appointment' is fine. The structured opening hours the app uses to decide whether the shop is open are set separately in Settings." />
      </div>

      <div style={{ fontSize: 12, color: C.sub, margin: '4px 0 8px' }}>
        Address <span style={{ color: C.muted }}>— in parts, because carrier registration matches it field by field</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 0.6fr 0.8fr', gap: '0 16px' }}>
        <Field labelText="Street" value={form.street} onChange={set('street')} placeholder="125 Main Street"
          tip="The street address on the business's tax filing. If the carrier registration says something different, it gets rejected." />
        <Field labelText="City" value={form.city} onChange={set('city')} placeholder="Riverton" />
        <Field labelText="State" value={form.region} onChange={set('region')} placeholder="NJ" maxLength={2}
          tip="Two-letter code, e.g. NJ. Not the spelled-out name." />
        <Field labelText="ZIP" value={form.postalCode} onChange={set('postalCode')} placeholder="07000"
          tip="Five digits, or 5+4 like 12345-6789." />
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, margin: '6px 0 12px', paddingTop: 12 }}>
        <div onClick={() => setShowReg(v => !v)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: C.muted, fontSize: 11 }}>{showReg ? '▲' : '▼'}</span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Carrier registration details</span>
          <span style={{ fontSize: 11, color: C.muted }}>
            optional now · required before the shop can text anyone
          </span>
        </div>
        {showReg && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
              A2P 10DLC registration takes days and can be rejected — usually over a legal
              name or address that does not match the business filing. Whatever is missing
              shows on the shop's checklist, so nothing here blocks creating the shop.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              {REG_FIELDS.map(([key, labelText, hint, tip, list, options]) => (
                <Field key={key} labelText={labelText} hint={hint} tip={tip} list={list} options={options}
                  value={form.registration[key]} onChange={setReg(key)} />
              ))}
            </div>
            <div style={{ fontSize: 12, color: C.sub, margin: '4px 0 8px' }}>
              Authorised representative <span style={{ color: C.muted }}>— the person the carrier filing names</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
              {REP_FIELDS.map(([key, labelText, tip, list, options]) => (
                <Field key={key} labelText={labelText} tip={tip} list={list} options={options}
                  value={form.registration.representative[key]} onChange={setRep(key)} />
              ))}
            </div>
          </div>
        )}
      </div>

      {checked && (
        <div style={{ fontSize: 12, marginBottom: 10, color: checked.ok ? C.ok : C.danger }}>
          {checked.ok
            ? `Looks good. Their booking page will be ${checked.bookingPath}`
            : (checked.message || 'That will not work.')}
        </div>
      )}
      {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button style={button(false)} onClick={check} disabled={busy}>Check</button>
        <button style={button(true)} onClick={create} disabled={busy || !(checked && checked.ok)}>
          {busy ? 'Working…' : 'Create shop'}
        </button>
        <span style={{ fontSize: 11, color: C.muted }}>
          Check first — creating is not reversible from here.
        </span>
      </div>
    </div>
  );
}

export default function AdminConsole() {
  const [admin, setAdmin] = useState(null);
  const [shops, setShops] = useState(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [notice, setNotice] = useState('');
  const [changingPw, setChangingPw] = useState(false);
  // The emailed reset link opens /admin?reset=<token>.
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get('reset') || '');

  // Every call carries the admin token and nothing else. A 401 means the token
  // expired or the admin was disabled — sign out rather than showing an empty
  // console, which is the failure mode the shop app already has an open bug for.
  const authFetch = useCallback(async (path, opts = {}) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const r = await fetch(`${API}${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    });
    if (r.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      setAdmin(null);
      throw new Error('Your session ended. Sign in again.');
    }
    return r;
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await authFetch('/api/admin/shops');
      const body = await r.json();
      if (!r.ok) throw new Error(body.error || 'Could not load shops.');
      setShops(body.shops);
      setError('');
    } catch (err) { setError(err.message); }
  }, [authFetch]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setReady(true); return; }
    fetch(`${API}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('expired'))))
      .then(b => setAdmin(b.admin))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setReady(true));
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const r = await authFetch('/api/admin/audit?limit=30');
      const body = await r.json();
      if (r.ok) setAudit(body.entries || []);
    } catch (_) { /* the history not loading must not blank the console */ }
  }, [authFetch]);

  useEffect(() => { if (admin) { load(); loadAudit(); } }, [admin, load, loadAudit]);

  if (!ready) return <Shell><div style={{ color: C.muted, fontSize: 13 }}>Loading…</div></Shell>;
  if (resetToken) {
    return <ResetPassword token={resetToken} onDone={() => {
      window.history.replaceState(null, '', '/admin');
      localStorage.removeItem(TOKEN_KEY);
      setAdmin(null);
      setResetToken('');
    }} />;
  }
  if (!admin) return <Login onSignedIn={setAdmin} />;

  return (
    <Shell>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.sub }}>Signed in as {admin.username}</div>
        <button style={{ ...button(false), marginLeft: 'auto', fontSize: 12 }}
          onClick={() => setChangingPw(v => !v)}>
          Change password
        </button>
        <button style={{ ...button(false), marginLeft: 8, fontSize: 12 }}
          onClick={() => { localStorage.removeItem(TOKEN_KEY); setAdmin(null); setShops(null); }}>
          Sign out
        </button>
      </div>

      {changingPw && <ChangePassword authFetch={authFetch} onClose={() => setChangingPw(false)} />}

      <UnmatchedSignups authFetch={authFetch} shops={shops} onLinked={() => { load(); loadAudit(); }} />

      <Intakes authFetch={authFetch} onShopCreated={() => { load(); loadAudit(); }} />

      <div style={{ marginBottom: 20 }}>
        <CreateShop authFetch={authFetch} onCreated={load} />
      </div>

      {notice && (
        <div style={{ fontSize: 12, color: C.ok, marginBottom: 10 }}>{notice}</div>
      )}
      <div style={{ fontSize: 13, color: C.sub, marginBottom: 10 }}>
        Shops · every line below is read from live state, not ticked by hand
      </div>
      {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      {shops === null && !error && <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>}
      {shops && shops.filter(s => !s.hidden).map(s => (
        <ShopCard key={s.id} shop={s} shops={shops} authFetch={authFetch}
          onSaved={(msg) => { setNotice(msg); load(); loadAudit(); }} />
      ))}
      {shops && shops.some(s => s.hidden) && (
        <button onClick={() => setShowHidden(v => !v)}
          style={{ background: 'none', border: 'none', padding: '4px 0', color: C.sub, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          {showHidden ? '▲ Hide the hidden shops' : `▼ Show hidden shops (${shops.filter(s => s.hidden).length})`}
        </button>
      )}
      {showHidden && shops && shops.filter(s => s.hidden).map(s => (
        <div key={s.id} style={{ opacity: 0.7 }}>
          <ShopCard shop={s} shops={shops} authFetch={authFetch}
            onSaved={(msg) => { setNotice(msg); load(); loadAudit(); }} />
        </div>
      ))}

      {/* Who changed what. Append only — there is no route that edits or removes
          an entry, which is the only thing that makes it worth reading. */}
      <div style={{ marginTop: 22 }}>
        <div onClick={() => setShowAudit(v => !v)}
          style={{ cursor: 'pointer', fontSize: 13, color: C.sub, marginBottom: 8 }}>
          <span style={{ color: C.muted, fontSize: 11 }}>{showAudit ? '▲' : '▼'}</span>{' '}
          History <span style={{ color: C.muted }}>— every change made from this console</span>
        </div>
        {showAudit && (
          <div style={box}>
            {!audit.length && <div style={{ fontSize: 12, color: C.muted }}>Nothing yet.</div>}
            {audit.map(e => (
              <div key={e.id} style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: 12, borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.muted, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap' }}>
                  {new Date(e.created_at).toLocaleString()}
                </span>
                <span style={{ color: C.text }}>{e.admin_username}</span>
                <span style={{ color: C.sub }}>{e.action}</span>
                {e.shop_id && <span style={{ color: C.muted }}>shop {e.shop_id}</span>}
                <span style={{ color: C.muted, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.detail && Object.keys(e.detail).length ? JSON.stringify(e.detail) : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
